import { PersonRequest, SurrogateContact } from "@plinknz/tah-website-elements";

import { AddressMeta } from "../../../components/form/types";
import { ADDRESS_FINDER_KEY } from "../../../components/survey-widgets/keys";
import {
  PageConfig,
  RadioGroupChoice,
} from "../../../components/content-blocks/forms/customer-form-types";
import { getPhone, PhoneType } from "../../../utility/phone";

export const APPLICANT_DETAILS = "applicantsDetails";
export const ADDRESS = "address";

export interface PersonalData {
  phone: string;
  email: string;
  addressfinder: AddressMeta;
}

interface Details {
  firstName: string;
  surname: string;
  phone?: string;
  email: string;
  hidePhone: boolean;
  memberNumber: number;
  visibleIf?: string;
}

function makeDetails({
  firstName,
  surname,
  phone,
  email,
  hidePhone,
  visibleIf,
  memberNumber,
}: Details): PageConfig {
  const id = `${memberNumber || ""}`;
  return {
    name: APPLICANT_DETAILS,
    title: "Applicant's Details - Taipitopito o te Kaitono",
    visibleIf,
    questions: [
      {
        type: "text",
        name: `firstName${id}`,
        title: "First Name",
        placeHolder: "Ingoa",
        defaultValue: firstName,
        readOnly: true,
        isRequired: true,
      },
      {
        type: "text",
        name: `surname${id}`,
        title: "Surname",
        placeHolder: "Ingoa Whānau",
        defaultValue: surname,
        readOnly: true,
        startWithNewLine: false,
        isRequired: true,
      },
      {
        type: "text",
        title: "Phone",
        name: `phone${id}`,
        placeHolder: "Waea",
        startWithNewLine: false,
        defaultValue: phone,
        visibleIf: hidePhone ? "1 = 0" : "1 = 1",
        isRequired: true,
      },
      {
        type: "text",
        name: `email${id}`,
        title: "Email",
        placeHolder: "Īmēra",
        validators: [
          {
            type: "email",
          },
        ],
        startWithNewLine: true,
        defaultValue: email,
        isRequired: true,
        readOnly: true,
      },
    ],
  };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function collapseSurrogate(data: any) {
  const id = data.memberChooser;
  if (!id) {
    return data;
  }

  return {
    ...data,
    firstName: data[`firstName${id}`],
    surname: data[`surname${id}`],
    phone: data[`phone${id}`],
    email: data[`email${id}`],
    id,
  };
}

export function personalDetailsConfig(member: PersonRequest): PageConfig[] {
  const memberName = `${member.firstName} ${member.surname}`;
  const email = member?.email?.length ? member.email[0].value : undefined;
  const surrogates = !!member?.emailSurrogate?.length;
  const phone = getPhone({ phone: member?.phone, type: PhoneType.Home });
  const cell = getPhone({ phone: member?.phone, type: PhoneType.Cell });

  const pages: PageConfig[] = [
    makeDetails({
      firstName: member.firstName,
      surname: member.surname,
      phone: phone?.value || cell?.value,
      email,
      hidePhone: false,
      visibleIf: surrogates ? `{memberChooser} = '${member.id}'` : "1 = 1",
      memberNumber: surrogates ? member.id : null,
    }),
  ];

  if (surrogates) {
    const choices: RadioGroupChoice[] = member.emailSurrogate.map(
      ({ firstName, surname, id }) => ({
        value: id,
        text: `${firstName} ${surname}`,
      })
    );
    choices.unshift({
      text: memberName,
      value: member.id.toString(),
    });
    const page: PageConfig = {
      name: "memberChooser",
      title: "Choose Member - Mō Wai?",
      questions: [
        {
          title:
            "You have multiple members registered to this email, please choose which one this application is for.",
          type: "radiogroup",
          name: "memberChooser",
          choices,
          isRequired: true,
        },
      ],
    };
    pages.unshift(page);

    for (const surrogateMember of member.emailSurrogate) {
      const { firstName, surname, id }: SurrogateContact = surrogateMember;
      pages.push(
        makeDetails({
          firstName,
          surname,
          hidePhone: true,
          email,
          visibleIf: `{memberChooser} = '${id}'`,
          memberNumber: id,
        })
      );
    }
  } else {
    let defaultValue;

    if (member?.address && member?.address.length) {
      const address = member?.address[0];
      defaultValue = {
        address_line_1: address.line1,
        address_line_2: address.line2,
        suburb: address.suburb,
        city: address.city,
        postcode: address.postcode,
        state: address.state,
        country: address.country,
      };
    }

    pages.push({
      name: ADDRESS,
      title: "Address - Wāhi Noho",
      questions: [
        {
          title: "Address",
          type: ADDRESS_FINDER_KEY,
          name: "addressfinder",
          isRequired: true,
          defaultValue,
        },
      ],
    });
  }

  return pages;
}
